import React, { useState, useEffect } from "react";
import QuestionAnalyticsChart from "./QuestionAnalyticsChart";
import "./AnalyticsComponents.css";
import { chartAPI } from "../../services/apiClient";

// Note: Advanced analytics components removed as they use non-existent APIs:
// - WordCloudViewer (surveyAPI.getOpenEndedWithUsers)
// - GridAnalytics (analyticsAPI.getFilteredQuestionAnalytics, getQuestionAnalyticsUnified)
// - reportTabAPI (removed - using simplified analyticsAPI instead)

const QuestionAnalyticsWithSettings = ({
  question,
  surveyId,
  questionSettings,
  globalSettings = {}, // Provide default to prevent errors
  onSettingsChange,
  questionOptions,
  index, // Kept for context if needed
}) => {
  // Local state for immediate UI feedback, backed by reliable backend saves
  const [localSettings, setLocalSettings] = useState({});
  const [showSettings, setShowSettings] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'saving', 'saved', 'error'

  // Check if this is an open-ended question
  const isOpenEnded = question.question_type === "open-ended";

  // Update local settings when props change
  useEffect(() => {
    const safeSettings = questionSettings || {};
    const mergedDefaults = {
      chartType: safeSettings.chartType || getDefaultChartType(question.question_type),
      showPercentages: safeSettings.showPercentages ?? true,
      showLegend: safeSettings.showLegend ?? true,
      showResponseDist: safeSettings.showResponseDist ?? true,
      showStatsTable: safeSettings.showStatsTable ?? true,
      showNA: safeSettings.showNA ?? true,
      ...safeSettings
    };

    // If this is an open-ended question and we're initializing settings,
    // set isHidden to true by default (exclude from PDF)
    if (isOpenEnded && !safeSettings.hasOwnProperty('isHidden')) {
      const initialSettings = {
        ...mergedDefaults,
        isHidden: true // Default to excluded from PDF for open-ended
      };
      setLocalSettings(initialSettings);

      // Save this default setting to backend immediately
      saveSettingsToBackend(initialSettings);
    } else {
      setLocalSettings(mergedDefaults);
    }
  }, [questionSettings, isOpenEnded, question.question_type]);

  // Save settings to backend with proper error handling
  const saveSettingsToBackend = async (settings) => {
    try {
      setSaveStatus("saving");

      // Call the parent component's onSettingsChange to save to backend
      const success = await onSettingsChange(null, settings);

      if (success !== false) { // success is true or undefined (async function completed)
        setSaveStatus("saved");

        // Clear success message after 3 seconds
        setTimeout(() => {
          setSaveStatus(null);
        }, 3000);
      } else {
        throw new Error("Save operation returned false");
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      setSaveStatus("error");

      // Clear error message after 5 seconds
      setTimeout(() => {
        setSaveStatus(null);
      }, 5000);
    }
  };

  const handleSettingChange = (field, value) => {
    // Update local settings for immediate visual changes
    const newSettings = {
      ...localSettings,
      [field]: value,
    };
    setLocalSettings(newSettings);

    // Save immediately to backend
    saveSettingsToBackend(newSettings);
  };

  const updateOptionColor = (optionIndex, color) => {
    const currentColors = Array.isArray(localSettings.customColors) ? localSettings.customColors : [];
    const newColors = [...currentColors];
    // Use default palette color for padding instead of chartColor
    const defaultPalette = [
      "#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0", "#9966FF",
      "#FF9F40", "#66FF66", "#FF66B3", "#3399FF", "#FF6666",
    ];
    while (newColors.length <= optionIndex) {
      newColors.push(defaultPalette[newColors.length % defaultPalette.length]);
    }
    newColors[optionIndex] = color;

    // Update local settings and save immediately
    handleSettingChange('customColors', newColors);
  };

  const updateSpecialColors = (colorType, colorKey, color) => {
    const newColorSettings = { ...(localSettings[colorType] || {}), [colorKey]: color };
    handleSettingChange(colorType, newColorSettings);
  };

  // Helper function to get the actual chart colors that will be used
  const getActualChartColors = () => {
    // Use the same default palette as QuestionAnalyticsChart component
    const defaultPalette = [
      "#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0", "#9966FF",
      "#FF9F40", "#66FF66", "#FF66B3", "#3399FF", "#FF6666",
    ];

    // For scale questions, use scale_points length
    let count = 0;
    if (isScale && question.scale_points) {
      count = question.scale_points.length;
    } else if (questionOptions && questionOptions.length > 0) {
      count = questionOptions.length;
    } else {
      return []; // No options to color
    }

    // Always prioritize saved custom colors from settings
    const savedCustomColors = questionSettings.customColors || localSettings.customColors || [];

    // Create result array with default colors first
    const result = [];
    for (let i = 0; i < count; i++) {
      const savedColor = savedCustomColors[i];
      const defaultColor = defaultPalette[i % defaultPalette.length];

      // Use saved color if it exists and is valid, otherwise use default
      if (savedColor && typeof savedColor === 'string' && savedColor.trim() !== '') {
        result.push(savedColor);
      } else {
        result.push(defaultColor);
      }
    }

    return result;
  };

  const getDefaultChartType = (questionType) => {
    switch (questionType) {
      case "single-choice":
      case "multiple-choice":
        return "bar";
      case "rating-scale":
      case "star-rating":
      case "slider":
      case "rating": // Slider question type in backend
      case "nps":
        return "bar";
      case "likert-scale":
        return "bar";
      case "scale": // Add scale question type support
        return "bar";
      default:
        return "bar";
    }
  };

  const isChartable = !["open-ended", "grid-single", "grid-multiple", "interactive-ranking"].includes(
    question.question_type
  );
  const hasStats = [
    "rating-scale",
    "star-rating",
    "star-rating-grid",
    "checkbox-grid",
    "likert-scale",
    "slider",
    "rating", // Slider question type in backend
    "numerical-input"
  ].includes(question.question_type);
  // Note: NPS has its own dedicated analysis display, so it doesn't use the generic "Show Statistics Table" checkbox
  const hasDistribution = [
    "single-choice",
    "multiple-choice",
    "multi-choice",
    "checkbox",
    "dropdown",
    "single-image-select",
    "multiple-image-select",
    "multi-image-select",
    "scale",
    "rating", // Slider question type in backend - has distribution table
    "nps"
  ].includes(question.question_type);
  const hasNAOption = [
    "rating-scale",
    "star-rating",
    "star-rating-grid",
    "likert-scale",
    "rating",
    "scale",
    "single-choice",
    "dropdown",
    "multi-choice",
    "multiple-choice",
    "checkbox",
    "single-image-select",
    "multiple-image-select",
    "numerical-input",
    "nps"
  ].includes(question.question_type);
  const isImageType = [
    "single-image-select",
    "multiple-image-select",
    "multi-image-select",
    "image-choice",
  ].includes(question.question_type);
  const isSlider = question.question_type === "slider" || question.question_type === "rating";
  const isNPS = question.question_type === "nps";
  const isStarRating = question.question_type === "star-rating";
  const isScale = question.question_type === "scale"; // Fixed to use correct question type

  const renderScaleColorOptions = () => {
    // For scale questions, use the actual scale points instead of generating numeric range
    const scalePoints = question.scale_points || ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'];
    // Default palette for fallback
    const defaultPalette = [
      "#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0", "#9966FF",
      "#FF9F40", "#66FF66", "#FF66B3", "#3399FF", "#FF6666",
    ];

    return (
      <div className="settings-group">
        <h5>Scale Option Colors:</h5>
        <div className="option-colors-grid">
          {scalePoints.map((point, index) => {
            const colorInputId = `scale-color-${question.id}-${index}`;
            return (
              <div
                key={`scale-${index}`}
                className="scale-color-item clickable-color-row"
                onClick={() => document.getElementById(colorInputId).click()}
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', padding: '4px', borderRadius: '4px', transition: 'background-color 0.2s' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                <input
                  id={colorInputId}
                  type="color"
                  value={
                    localSettings.customColors?.[index] ||
                    defaultPalette[index % defaultPalette.length]
                  }
                  onChange={(e) => {
                    updateOptionColor(index, e.target.value);
                  }}
                  style={{ width: '24px', height: '24px', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                />
                <span style={{ userSelect: 'none', cursor: 'pointer', flex: 1 }}>{point}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderNPSColorOptions = () => {
    const npsPoints = Array.from({ length: 11 }, (_, i) => i);

    return (
      <div className="settings-group">
        <h5>NPS Point Colors:</h5>
        <div className="nps-colors-grid">
          {npsPoints.map((point) => {
            const colorInputId = `nps-color-${question.id}-${point}`;
            return (
              <div
                key={`nps-${point}`}
                className="nps-color-item clickable-color-row"
                onClick={() => document.getElementById(colorInputId).click()}
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', padding: '4px', borderRadius: '4px', transition: 'background-color 0.2s' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                <input
                  id={colorInputId}
                  type="color"
                  value={
                    localSettings.npsColors?.[point] ||
                    (point <= 6
                      ? "#ff4d4d" // Detractors (0-6)
                      : point <= 8
                        ? "#ffd700" // Passives (7-8)
                        : "#4CAF50") // Promoters (9-10)
                  }
                  onChange={(e) => {
                    const currentColors = { ...(localSettings.npsColors || {}) };
                    currentColors[point] = e.target.value;
                    handleSettingChange("npsColors", currentColors);
                  }}
                  style={{ width: '24px', height: '24px', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                />
                <span style={{ userSelect: 'none', cursor: 'pointer' }}>{point}</span>
              </div>
            );
          })}
        </div>

        <div className="nps-legend">
          <div className="nps-legend-item detractor">Detractors (0-6)</div>
          <div className="nps-legend-item passive">Passives (7-8)</div>
          <div className="nps-legend-item promoter">Promoters (9-10)</div>
        </div>
      </div>
    );
  };

  const renderStarRatingColorOptions = () => {
    const maxStars = question.max_stars || 5;
    const starPoints = Array.from({ length: maxStars }, (_, i) => i + 1);

    return (
      <div className="settings-group">
        <h5>Star Rating Colors:</h5>
        <div className="star-colors-grid">
          {starPoints.map((stars) => {
            const colorInputId = `star-color-${question.id}-${stars}`;
            return (
              <div
                key={`star-${stars}`}
                className="star-color-item clickable-color-row"
                onClick={() => document.getElementById(colorInputId).click()}
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', padding: '4px', borderRadius: '4px', transition: 'background-color 0.2s' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                <input
                  id={colorInputId}
                  type="color"
                  value={localSettings.starColors?.[stars] || "#ffd700"}
                  onChange={(e) => {
                    updateSpecialColors("starColors", stars, e.target.value);
                  }}
                  style={{ width: '24px', height: '24px', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                />
                <span style={{ userSelect: 'none', cursor: 'pointer' }}>
                  {stars} {stars === 1 ? "Star" : "Stars"}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderSliderColorOptions = () => {
    // Handle both "slider" and "rating" question types with different field names
    const maxValue = question.max_value || question.rating_end || 100;
    const minValue = question.min_value || question.rating_start || 0;
    const step = question.step || question.rating_step || 1;
    const sliderPoints = [];
    for (let i = minValue; i <= maxValue; i += step) {
      sliderPoints.push(i);
    }

    return (
      <> </>
    );
  };

  const renderChartSettings = () => {
    const titleOnlyTypes = ["radio-grid", "star-rating", "star-rating-grid"];
    const showOnlyTitle = titleOnlyTypes.includes(question.question_type);
    const allowShowNA = hasNAOption && !titleOnlyTypes.includes(question.question_type);

    return (
      <div className="settings-panel">
        <h4 className="settings-title">Question Settings</h4>
        <div className="settings-content">
          <div className="settings-group">
            <div className="input-group">
              <label htmlFor={`qTitle-${question.id}`}>Custom Title:</label>
              <input
                id={`qTitle-${question.id}`}
                type="text"
                value={localSettings.customTitle || ""}
                onChange={(e) =>
                  handleSettingChange("customTitle", e.target.value)
                }
                placeholder="Custom title (optional)"
              />
            </div>
          </div>

          {!showOnlyTitle ? (
            <>
              {isChartable && !isNPS && (
                <div className="settings-group">
                  <div className="input-group">
                    <label htmlFor={`qChartType-${question.id}`}>
                      Chart Type:
                    </label>
                    <select
                      id={`qChartType-${question.id}`}
                      value={
                        localSettings.chartType ||
                        getDefaultChartType(question.question_type)
                      }
                      onChange={(e) =>
                        handleSettingChange("chartType", e.target.value)
                      }
                    >
                      <option value="bar">Bar</option>
                      <option value="pie">Pie</option>
                      <option value="doughnut">Doughnut</option>
                      <option value="line">Line</option>
                      <option value="none">No Chart</option>
                    </select>
                  </div>

                  <div className="checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={
                          localSettings.showPercentages !== undefined
                            ? localSettings.showPercentages
                            : true
                        }
                        onChange={(e) =>
                          handleSettingChange("showPercentages", e.target.checked)
                        }
                      />
                      Show Percentages
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={
                          localSettings.showLegend !== undefined
                            ? localSettings.showLegend
                            : true
                        }
                        onChange={(e) =>
                          handleSettingChange("showLegend", e.target.checked)
                        }
                      />
                      Show Legend
                    </label>
                  </div>
                </div>
              )}

              {hasStats && (
                <div className="settings-group">
                  <div className="checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={
                          localSettings.showStatsTable !== undefined
                            ? localSettings.showStatsTable
                            : true
                        }
                        onChange={(e) =>
                          handleSettingChange("showStatsTable", e.target.checked)
                        }
                      />
                      Show Statistics Table
                    </label>
                  </div>
                </div>
              )}

              <div className="settings-group">
                <div className="checkbox-group">
                  {(hasDistribution || isNPS) && (
                    <label>
                      <input
                        type="checkbox"
                        checked={
                          localSettings.showResponseDist !== undefined
                            ? localSettings.showResponseDist
                            : true
                        }
                        onChange={(e) =>
                          handleSettingChange("showResponseDist", e.target.checked)
                        }
                      />
                      Show Distribution Table
                    </label>
                  )}

                  {allowShowNA && (
                    <label>
                      <input
                        type="checkbox"
                        checked={
                          localSettings.showNA !== undefined
                            ? localSettings.showNA
                            : true
                        }
                        onChange={(e) =>
                          handleSettingChange("showNA", e.target.checked)
                        }
                      />
                      Show 'N/A' Responses
                    </label>
                  )}
                </div>
              </div>
            </>
          ) : null}

          <div className="settings-group" style={{ borderTop: '1px solid #eee', paddingTop: '15px', marginTop: '20px' }}>
            {saveStatus === 'saving' && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6c757d', fontSize: '14px' }}>
                <i className="ri-loader-4-line" style={{ marginRight: '5px', animation: 'spin 1s linear infinite' }}></i>
                Saving...
              </div>
            )}
            {saveStatus === 'saved' && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#28a745', fontSize: '14px' }}>
                <i className="ri-check-line" style={{ marginRight: '5px' }}></i>
                Settings saved
              </div>
            )}
            {saveStatus === 'error' && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#dc3545', fontSize: '14px' }}>
                <i className="ri-error-warning-line" style={{ marginRight: '5px' }}></i>
                Error saving settings
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="question-analytics-container">
      <div className="question-analytics-header">
        <div className="header-content">
          {/* Drag Handle */}
          <span className="drag-handle" style={{
            cursor: 'move',
            marginRight: '8px',
            fontSize: '16px',
            color: '#666',
            userSelect: 'none',
            padding: '4px',
            borderRadius: '3px',
            transition: 'all 0.2s ease'
          }}>
            ⋮⋮
          </span>
          <strong>#{localSettings.displayOrder || question.sequence_number || "N/A"}:</strong>{" "}
          {(localSettings.customTitle && localSettings.customTitle.trim()) || question.question_text}
        </div>

        {/* Display Order & PDF Include Controls */}
        <div className="header-controls" style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
          <label className="order-control" style={{ color: 'black', fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '5px' }}>
            Order:
            <input
              type="number"
              value={localSettings.displayOrder || ''}
              onChange={(e) => handleSettingChange('displayOrder', e.target.value)}
              className="order-input"
              min="1"
              placeholder="auto"
              style={{ width: '60px', padding: '4px', border: '1px solid #ccc', borderRadius: '3px' }}
            />
          </label>

          <label className="pdf-include-control" style={{
            color: 'black',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            backgroundColor: localSettings.isHidden !== true ? '#e8f5e8' : '#f8e8e8',
            borderRadius: '5px',
            border: `1px solid ${localSettings.isHidden !== true ? '#4CAF50' : '#f44336'}`,
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              checked={localSettings.isHidden !== true} // Checked if NOT hidden
              onChange={(e) => handleSettingChange('isHidden', !e.target.checked)}
              style={{ marginRight: '0' }}
            />
            <i className={`ri-file-pdf-line`} style={{ fontSize: '16px' }}></i>
            Include in PDF Export
          </label>

          {/* Only show settings toggle for non-open-ended questions */}
          {!isOpenEnded && (
            <label className="settings-toggle" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={showSettings}
                onChange={() => setShowSettings(!showSettings)}
              />
              <span className="toggle-slider"></span>
              <span className="toggle-label" style={{ color: 'black', fontSize: '14px' }}>
                {showSettings ? 'Hide Settings' : 'Show Settings'}
              </span>
            </label>
          )}
        </div>
      </div>
      <div className={`question-analytics-grid ${showSettings ? 'with-settings' : ''}`}>
        <div className="chart-column">
          {/* QuestionAnalyticsChart handles all question types including grids, open-ended, etc. */}
          <QuestionAnalyticsChart
            surveyId={surveyId}
            questionId={question.id}
            className="question-chart"
            hideCustomization={true}
            settings={{
              chartType: localSettings.chartType || globalSettings.chartType,
              ...globalSettings, // Pass all global settings
              ...localSettings   // Override with local question-specific settings - this ensures immediate visual feedback
            }}
          />
        </div>
        {showSettings && !isOpenEnded && (
          <div className="settings-column">
            <div className="settings-panel">
              <div className="settings-content">
                {renderChartSettings()}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Save status indicator */}
      {saveStatus && (
        <div className="save-status-indicator" style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          padding: '10px 20px',
          background: saveStatus === 'saving' ? '#f8f9fa' :
            saveStatus === 'saved' ? '#d4edda' : '#f8d7da',
          color: saveStatus === 'saving' ? '#6c757d' :
            saveStatus === 'saved' ? '#155724' : '#721c24',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          zIndex: 1000,
          transition: 'all 0.3s ease'
        }}>
          {saveStatus === 'saving' && <i className="ri-loader-4-line" style={{ marginRight: '8px', animation: 'spin 1s linear infinite' }}></i>}
          {saveStatus === 'saved' && <i className="ri-check-line" style={{ marginRight: '8px' }}></i>}
          {saveStatus === 'error' && <i className="ri-error-warning-line" style={{ marginRight: '8px' }}></i>}
          {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Settings saved' : 'Error saving settings'}
        </div>
      )}
    </div>
  );
};

export default QuestionAnalyticsWithSettings;
